import React from 'react';

import { Box, ClassNameMap, Typography } from '@mui/material';
import { MatchingProfileData } from '@uplift/api';
import { useIsMobile, useL10n } from '@uplift/hooks';

import { PreferredInsuranceMatch } from '../../preferred-insurance-match';
import { MatchingAvatar } from './avatar';
import { ProviderHeader } from './provider-header';
import { useNextAvailableDateFormatted } from './provider-item.hooks';
import { useProviderItemDesktopLayout, useProviderItemMobileLayout } from './styles';
import { ProviderMedicalRole } from '@uplift/types';

type ProviderItemProps = {
  onView: () => void;
  preferredInsuranceCompanyId?: string;
  stateId: string;
  matchingProfile: MatchingProfileData;
  classes?: Partial<ClassNameMap<'root'>>;
  isBookingSponsored?: boolean;
};

type ProviderItemLayoutProps = {
  classes?: Partial<ClassNameMap<'root'>>;
  onClick: () => void;
  avatar: React.ReactNode;
  providerHeader: React.ReactNode;
  acceptsInsurance: React.ReactNode;
  bio: React.ReactNode;
  specialties: React.ReactNode;
  nextAvailable: React.ReactNode;
};

const ProviderItemMobileLayout: React.FC<ProviderItemLayoutProps> = ({
  classes,
  onClick,
  avatar,
  providerHeader,
  acceptsInsurance,
  bio,
  specialties,
  nextAvailable,
}) => {
  const { classes: layoutClasses } = useProviderItemMobileLayout({ classes: classes }, { props: { classes: classes } });

  return (
    <Box className={layoutClasses.root} onClick={onClick}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {avatar}
        </Box>
        {acceptsInsurance ? (
          <Box>
            {acceptsInsurance}
          </Box>
        ) : <Box />}
      </Box>
      <Box>
        {providerHeader}
      </Box>
      <Box className={layoutClasses.bio}>
        {bio}
      </Box>
      <Box sx={{ mt: 1 }} className={layoutClasses.specialties}>
        {specialties}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1.3, mt: 1, bgcolor: 'grey.100' }}>
        {nextAvailable}
      </Box>
    </Box>
  );
};


const ProviderItemDesktopLayout: React.VFC<ProviderItemLayoutProps> = ({
  classes,
  onClick,
  avatar,
  providerHeader,
  acceptsInsurance,
  bio,
  specialties,
}) => {
  const { classes: layoutClasses } = useProviderItemDesktopLayout({ classes: classes }, { props: { classes: classes } });

  return (
    <Box
      className={layoutClasses.root}
      onClick={onClick}
    >
      <Box>
        {avatar}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {providerHeader}
          </Box>
          {acceptsInsurance ? (
            <Box sx={{ mt: 1, ml: 1 }}>
              {acceptsInsurance}
            </Box>
          ) : <Box />}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box className={layoutClasses.bio}>
            {bio}
          </Box>
        </Box>
        <Box className={layoutClasses.specialties}>
          {specialties}
        </Box>
      </Box>
    </Box>
  );
};

const ProviderItemLayout: React.VFC<ProviderItemLayoutProps> = (props) => {
  const isMobile = useIsMobile('sm');

  if (isMobile) {
    return <ProviderItemMobileLayout {...props} />;
  }

  return <ProviderItemDesktopLayout {...props} />;
};


export const ProviderItem: React.FC<ProviderItemProps> = ({ matchingProfile, onView, preferredInsuranceCompanyId, stateId, isBookingSponsored, ...props }) => {
  const l10n = useL10n();
  const nextAppointmentDate = useNextAvailableDateFormatted(matchingProfile?.nextAvailableDate);
  const bio = matchingProfile.profile.shortBio ?? 'No bio';
  const insuranceCompany = matchingProfile.supportedInsuranceCompanies?.find((insuranceCompany) => insuranceCompany.id === preferredInsuranceCompanyId);
  const specialties = matchingProfile.specialties.map((specialty) => specialty.name).join(', ');

  return (
    <ProviderItemLayout
      classes={props.classes}
      onClick={onView}
      avatar={<MatchingAvatar avatarUrl={matchingProfile.avatarAsset?.url} nextAvailableDate={nextAppointmentDate} />}
      providerHeader={(
        <ProviderHeader
          providerName={matchingProfile.nameDisplay.defaultDisplay}
          pronouns={matchingProfile.profile.pronouns}
          licenses={matchingProfile.licenses}
          stateId={stateId}
          medicalRole={matchingProfile.user.medicalRole ?? ProviderMedicalRole.therapist}
        />
      )}
      acceptsInsurance={!isBookingSponsored && <PreferredInsuranceMatch insuranceCompany={insuranceCompany} />}
      bio={bio}
      specialties={<><Typography color="secondary" fontWeight={700} component="span">{l10n.matching.providers.specialties}</Typography>: {specialties}</>}
      nextAvailable={<>{l10n.matching.providers.nextAvailable} {nextAppointmentDate}</>}
    />
  );
};
